import { autocompletion, closeBrackets, closeBracketsKeymap, completionKeymap } from '@codemirror/autocomplete';
import { defaultKeymap, indentWithTab } from '@codemirror/commands';
import { bracketMatching, defaultHighlightStyle, foldGutter, foldKeymap, indentOnInput, LanguageDescription, syntaxHighlighting } from '@codemirror/language';
import { languages } from "@codemirror/language-data";
import { lintKeymap } from '@codemirror/lint';
import { highlightSelectionMatches } from '@codemirror/search';
import { EditorState as CodeMirrorEditorState, Compartment, Extension } from '@codemirror/state';
import { EditorView as CodeMirrorEditorView, keymap as codeMirrorKeymap, crosshairCursor, drawSelection, dropCursor, highlightActiveLine, highlightActiveLineGutter, highlightSpecialChars, KeyBinding, lineNumbers, rectangularSelection, ViewUpdate } from '@codemirror/view';
import { toAttributeBoolean } from '@common/util/schema';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { EditableNodeViewOptions } from '@portal-core/text-editor/node-views/models/editable-node-view-options.model';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { exitCode } from 'prosemirror-commands';
import { redo, undo } from 'prosemirror-history';
import { Node } from 'prosemirror-model';
import { Selection, TextSelection } from 'prosemirror-state';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';

type ExtensionFactory = () => Extension;

const codeMirrorExtensionCache: WeakMap<ExtensionFactory, Extension> = new WeakMap();
const codeMirrorGutterBorderThemeCache: Map<string, Extension> = new Map();
const EmptyExtension: Extension = [];

function getOrCreateExtension(factory: ExtensionFactory): Extension {
  const cachedExtension = codeMirrorExtensionCache.get(factory);

  if (cachedExtension) {
    return cachedExtension;
  }

  const newExtension = factory();
  codeMirrorExtensionCache.set(factory, newExtension);
  return newExtension;
}

function getOrCreateGutterBorderThemeExtension(border: string): Extension {
  const normalizedBorder = border.trim();
  const cachedExtension = codeMirrorGutterBorderThemeCache.get(normalizedBorder);

  if (cachedExtension) {
    return cachedExtension;
  }

  const newExtension = CodeMirrorEditorView.theme({
    '.cm-gutters': {
      borderRight: normalizedBorder
    }
  });

  codeMirrorGutterBorderThemeCache.set(normalizedBorder, newExtension);
  return newExtension;
}

export class CodeSnippetBodyNodeView extends NodeViewBase implements NodeView {
  private codeMirrorEditorView: CodeMirrorEditorView
  private editableCompartment: Compartment = new Compartment();
  private gutterBorderThemeCompartment: Compartment = new Compartment();
  private langCompartment: Compartment = new Compartment();
  /** Tracks the current language load request to ensure only the latest request is applied. */
  private langLoadRequestId: number = 0;
  private lineNumbersCompartment: Compartment = new Compartment();
  /** Used to avoid an update loop between the outer and inner editor. */
  private updating: boolean = false;

  /** Opt-in to sync the node when the stylesheet changes. */
  get syncNodeOnStylesheetChanges(): boolean {
    return true;
  }

  constructor(
    node: Node,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>,
    private options: EditableNodeViewOptions
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    this.codeMirrorEditorView = new CodeMirrorEditorView({
      parent: this.dom,
      state: this.createEditorState(node)
    });

    // Sync the node
    this.syncNode(node);
  }

  destroy() {
    super.destroy();
    this.codeMirrorEditorView.destroy();
  }

  onEditorEvent(event: EditorEvent) {
    if (event.type === 'editable-changed') {
      this.updateCodeMirrorEditable(event.data.editable);
    }
  }

  syncNode(node: Node) {
    this.reconfigureCompartmentIfNeeded(this.lineNumbersCompartment, this.getCodeLineNumberExtension(node));
    this.reconfigureCompartmentIfNeeded(this.gutterBorderThemeCompartment, this.getCodeBorderExtension());
    this.reconfigureLangCompartment();

    this.updateEditorContent(node);
  }

  private reconfigureCompartmentIfNeeded(compartment: Compartment, targetExtension: Extension) {
    const currentExtension = compartment.get(this.codeMirrorEditorView.state);

    if (currentExtension !== targetExtension) {
      this.updating = true;

      this.codeMirrorEditorView.dispatch({
        effects: compartment.reconfigure(targetExtension)
      });

      this.updating = false;
    }
  }

  private reconfigureLangCompartment() {
    // Store the latest request id. If a newer request starts before this one resolves then this result must be ignored to avoid applying the wrong language.
    const requestId = ++this.langLoadRequestId;
    const langName = this.getCodeLangName();

    this.loadExtensionForLang(langName).then((languageExtension) => {
      // Ignore old requests and do nothing if the node view has been destroyed
      if (this.isDestroyed || requestId !== this.langLoadRequestId) {
        return;
      }

      this.reconfigureCompartmentIfNeeded(this.langCompartment, languageExtension ?? EmptyExtension);
    }).catch(() => {
      // Ignore old requests and do nothing if the node view has been destroyed
      if (this.isDestroyed || requestId !== this.langLoadRequestId) {
        return;
      }

      this.reconfigureCompartmentIfNeeded(this.langCompartment, EmptyExtension);
    });
  }

  private loadExtensionForLang(langName: string): Promise<Extension> {
    const langDesc = LanguageDescription.matchLanguageName(languages, langName);

    // If the language isn't supported then return an empty extension
    if (!langDesc) {
      return Promise.resolve(EmptyExtension);
    }

    // If the language has already been loaded then it will be stored in the support property
    if (langDesc.support) {
      return Promise.resolve(langDesc.support);
    }

    // Otherwise load the language support asynchronously
    return langDesc.load();
  }

  /**
   * Create the CodeMirror editor state based on the given node, configuring the necessary extensions including keymaps, language support, and editor behavior.
   * The keymaps and extensions mimic CodeMirror's basicSetup but exclude extensions that do not make sense as an embedded code editor.
   * For example, the history extension is not included since undo/redo is forwarded to the outer ProseMirror editor.
   * Other keymaps and extensions are included to sync with the ProseMirror node and outer editor.
   * https://github.com/codemirror/basic-setup/blob/main/src/codemirror.ts
   * @param node The ProseMirror node to create the editor state for.
   * @returns The created CodeMirror editor state.
   */
  private createEditorState(node: Node): CodeMirrorEditorState {
    return CodeMirrorEditorState.create({
      doc: '',
      extensions: [
        codeMirrorKeymap.of([
          ...this.codeMirrorKeymap(),
          ...closeBracketsKeymap,
          ...defaultKeymap,
          ...foldKeymap,
          ...completionKeymap,
          ...lintKeymap,
          indentWithTab
        ]),
        this.lineNumbersCompartment.of(this.getCodeLineNumberExtension(node)),
        this.editableCompartment.of(this.getCodeMirrorEditableExtensions(this.options.editable())),
        highlightActiveLineGutter(),
        highlightSpecialChars(),
        foldGutter(),
        drawSelection(),
        dropCursor(),
        CodeMirrorEditorState.allowMultipleSelections.of(true),
        indentOnInput(),
        syntaxHighlighting(defaultHighlightStyle),
        bracketMatching(),
        closeBrackets(),
        autocompletion(),
        rectangularSelection(),
        crosshairCursor(),
        highlightActiveLine(),
        highlightSelectionMatches(),
        this.gutterBorderThemeCompartment.of(this.getCodeBorderExtension()),
        this.langCompartment.of(EmptyExtension),
        CodeMirrorEditorView.updateListener.of(update => {
          this.forwardUpdate(update);
        })
      ]
    });
  }

  private getCodeMirrorEditableExtensions(editable: boolean): Extension {
    return [
      CodeMirrorEditorView.editable.of(editable),
      CodeMirrorEditorState.readOnly.of(!editable)
    ];
  }

  private updateCodeMirrorEditable(editable: boolean): void {
    this.reconfigureCompartmentIfNeeded(this.editableCompartment, this.getCodeMirrorEditableExtensions(editable));
  }

  private getCodeBorderExtension(): Extension {
    const border = getComputedStyle(this.dom).getPropertyValue('--mc-code-border') || '#AAA 2px solid';
    return border ? getOrCreateGutterBorderThemeExtension(border) : EmptyExtension;
  }

  private getCodeLineNumberExtension(node: Node): Extension {
    return toAttributeBoolean(node.attrs['MadCap:useLineNumbers']) ? getOrCreateExtension(lineNumbers) : EmptyExtension;
  }

  private getCodeLangName(): string {
    const lang = getComputedStyle(this.dom).getPropertyValue('--mc-code-lang');

    switch (lang?.toLowerCase()) {
      case 'actionscript':
        return 'JavaScript';
      case 'bash':
        return 'shell';
      case 'css':
        return 'CSS';
      case 'cpp':
        return 'C++';
      case 'csharp':
        return 'C#';
      case 'd':
        return 'D';
      case 'erlang':
        return 'Erlang';
      case 'go':
        return 'Go';
      case 'haskell':
        return 'Haskell';
      case 'html':
        return 'HTML';
      case 'java':
        return 'Java';
      case 'javascript':
        return 'JavaScript';
      case 'json':
        return 'JSON';
      case 'latex':
        return 'LaTeX';
      case 'lua':
        return 'Lua';
      case 'markdown':
        return 'Markdown';
      case 'objectivec':
        return 'Objective-C';
      case 'ocaml':
        return 'OCaml';
      case 'patch':
        return 'diff';
      case 'php':
        return 'PHP';
      case 'plaintext':
        return '';
      case 'powershell':
        return 'PowerShell';
      case 'python':
        return 'Python';
      case 'r':
        return 'R';
      case 'ruby':
        return 'Ruby';
      case 'scala':
        return 'Scala';
      case 'sql':
        return 'MS SQL';
      case 'tsql':
        return 'MS SQL';
      case 'vb':
        return 'VB.NET';
      case 'xml':
        return 'XML';
      // Languages supported by FD but not FO
      case 'applescript':
      case 'aspx':
      case 'batch':
      case 'coco':
      case 'makefile':
      case 'matlab':
      case 'xmldoc':
      default:
        return '';
    }
  }

  /*
   * The functions below are taken from the ProseMirror example for a CodeMirror node view.
   * They remain mostly untouched except for added types, variable names, and code formatting.
   * https://prosemirror.net/examples/codemirror/
   * https://github.com/ProseMirror/website/tree/master/example/codemirror
   */
  selectNode() {
    this.codeMirrorEditorView.focus();
  }

  setSelection(anchor: number, head: number) {
    this.codeMirrorEditorView.focus();
    this.updating = true;
    this.codeMirrorEditorView.dispatch({ selection: { anchor, head } });
    this.updating = false;
  }

  stopEvent(): boolean {
    return true;
  }

  private codeMirrorKeymap(): KeyBinding[] {
    const view = this.editorView;

    return [
      { key: 'ArrowUp', run: () => this.maybeEscape('line', -1) },
      { key: 'ArrowLeft', run: () => this.maybeEscape('char', -1) },
      { key: 'ArrowDown', run: () => this.maybeEscape('line', 1) },
      { key: 'ArrowRight', run: () => this.maybeEscape('char', 1) },
      {
        key: 'Ctrl-Enter', run: () => {
          if (!exitCode(view.state, view.dispatch)) {
            return false;
          }
          view.focus();
          return true;
        }
      },
      {
        key: 'Ctrl-z', mac: 'Cmd-z',
        run: () => undo(view.state, view.dispatch)
      },
      {
        key: 'Shift-Ctrl-z', mac: 'Shift-Cmd-z',
        run: () => redo(view.state, view.dispatch)
      },
      {
        key: 'Ctrl-y', mac: 'Cmd-y',
        run: () => redo(view.state, view.dispatch)
      }
    ];
  }

  private maybeEscape(unit: 'char' | 'line', dir: -1 | 1): false | undefined {
    const { state } = this.codeMirrorEditorView;
    let { main }: any = state.selection;

    if (!main.empty) {
      return false;
    }

    if (unit === 'line') {
      main = state.doc.lineAt(main.head);
    }

    if (dir < 0 ? main.from > 0 : main.to < state.doc.length) {
      return false;
    }

    let targetPos = this.getPos() + (dir < 0 ? 0 : this.node.nodeSize);
    let selection = Selection.near(this.editorView.state.doc.resolve(targetPos), dir);
    let tr = this.editorView.state.tr.setSelection(selection).scrollIntoView();
    this.editorView.dispatch(tr);
    this.editorView.focus();
  }

  private updateEditorContent(node: Node) {
    if (this.updating) {
      return;
    }

    const newText = node.textContent, curText = this.codeMirrorEditorView.state.doc.toString();
    if (newText !== curText) {
      let start = 0;
      let curEnd = curText.length;
      let newEnd = newText.length;

      while (start < curEnd && curText.charCodeAt(start) === newText.charCodeAt(start)) {
        ++start;
      }

      while (curEnd > start && newEnd > start && curText.charCodeAt(curEnd - 1) === newText.charCodeAt(newEnd - 1)) {
        curEnd--;
        newEnd--;
      }

      this.updating = true;

      this.codeMirrorEditorView.dispatch({
        changes: {
          from: start, to: curEnd,
          insert: newText.slice(start, newEnd)
        }
      });

      this.updating = false;
    }
  }

  private forwardUpdate(update: ViewUpdate) {
    if (this.updating || !this.codeMirrorEditorView.hasFocus) {
      return;
    }

    let offset = this.getPos() + 1;
    const { main } = update.state.selection;
    const selFrom = offset + main.from;
    const selTo = offset + main.to;
    const pmSel = this.editorView.state.selection;
    const schema = this.editorView.state.schema;

    if (update.docChanged || pmSel.from !== selFrom || pmSel.to !== selTo) {
      let tr = this.editorView.state.tr;

      update.changes.iterChanges((fromA, toA, fromB, toB, text) => {
        if (text.length) {
          tr.replaceWith(offset + fromA, offset + toA, schema.text(text.toString()));
        } else {
          tr.delete(offset + fromA, offset + toA);
        }

        offset += (toB - fromB) - (toA - fromA);
      });

      tr.setSelection(TextSelection.create(tr.doc, selFrom, selTo))
      this.editorView.dispatch(tr)
    }
  }
}
